import React, { useState, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import SandboxCanvas from "@/components/SandboxCanvas";
import type { ChatMessage, WorkflowData } from "@/types";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Send, Sparkles, Workflow, Lightbulb } from "lucide-react";
import { useOrganization } from "@/contexts/OrganizationContext";
import DiocesanPlannerWizard from "./DiocesanPlannerWizard";

// Get or create a persistent user identifier for analytics
const getUserIdentifier = (): string => {
  const storageKey = 'bwcx_sandbox_user_id';
  let userId = localStorage.getItem(storageKey);
  if (!userId) {
    userId = crypto.randomUUID();
    localStorage.setItem(storageKey, userId);
  }
  return userId;
};

const EXAMPLE_PROMPT = "Our food bank needs to match donations with families based on dietary needs and family size. We want to automate intake forms and send personalized pickup notifications.";

const Sandbox: React.FC = () => {
  const { organization } = useOrganization();
  
  // If planner_mode is 'wizard', render the DiocesanPlannerWizard instead
  if (organization?.settings?.planner_mode === 'wizard') {
    return <DiocesanPlannerWizard />;
  }
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [workflowData, setWorkflowData] = useState<WorkflowData | null>(null);
  const [analysis, setAnalysis] = useState("");
  const [summary, setSummary] = useState("");
  const [showMobileWorkflow, setShowMobileWorkflow] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const pendingExternalPromptRef = useRef<string | null>(null);

  // Get planner label from organization settings
  const plannerLabel = organization?.settings?.planner_label || "Planner";

  // Listen for external sandbox prompts from ChatPane
  useEffect(() => {
    const handleExternalPrompt = (event: CustomEvent<{ prompt: string }>) => {
      const prompt = event.detail?.prompt;
      if (prompt) {
        pendingExternalPromptRef.current = prompt;
        setInput(prompt);
      }
    };
    
    window.addEventListener("executeSandboxPrompt", handleExternalPrompt as EventListener);
    return () => window.removeEventListener("executeSandboxPrompt", handleExternalPrompt as EventListener);
  }, []);

  // Auto-execute when external prompt is set
  useEffect(() => {
    if (pendingExternalPromptRef.current && input === pendingExternalPromptRef.current && !loading) {
      pendingExternalPromptRef.current = null;
      const timer = setTimeout(() => {
        handleSend(input);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [input, loading]);

  const handleSend = async (customMessage?: string) => {
    const messageToSend = customMessage || input.trim();
    if (!messageToSend) return;
    
    // Mark as started on first message
    if (!hasStarted) {
      setHasStarted(true);
    }
    
    const userMsg: ChatMessage = { 
      id: Date.now().toString(), 
      role: "user", 
      content: messageToSend, 
      timestamp: Date.now() 
    };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('sandbox-solver', {
        body: {
          messages: [
            ...messages.map(m => ({ role: m.role, content: m.content })),
            { role: 'user', content: messageToSend }
          ],
          userIdentifier: getUserIdentifier(),
          organization_id: organization?.id
        }
      });

      if (error) throw error;

      // Extract workflow data from response
      if (data.workflow) {
        setWorkflowData(data.workflow);
      }
      if (data.analysis) {
        setAnalysis(data.analysis);
      }
      if (data.summary) {
        setSummary(data.summary);
      }

      const response = data.summary || data.analysis || "Solution generated.";

      const aiMsg: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: response,
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, aiMsg]);
    } catch (error) {
      console.error('Error calling sandbox-solver:', error);
      const errorMsg: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: "I'm having trouble connecting. Please try again.",
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefineClick = () => {
    const refinePrompt = "Refine this workflow to be more detailed and execution-ready.";
    handleSend(refinePrompt);
  };

  const handleExampleClick = () => {
    setInput(EXAMPLE_PROMPT);
    inputRef.current?.focus();
  };

  // Welcome Screen - shown before first message
  if (!hasStarted && !loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center p-6">
        <div className="max-w-2xl w-full text-center space-y-8">
          {/* Icon */}
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Workflow className="w-8 h-8 text-primary" />
            </div>
          </div>

          {/* Header */}
          <div className="space-y-3">
            <h1 className="text-4xl font-bold font-display text-foreground">
              {plannerLabel}
            </h1>
            <p className="text-lg text-muted-foreground max-w-lg mx-auto">
              Describe your nonprofit's challenge and visualize a workflow to build an AI solution
            </p>
          </div>

          {/* Input Form */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSend();
            }}
            className="space-y-4"
          >
            <Textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Describe your challenge..."
              disabled={loading}
              className="min-h-[140px] resize-none text-base"
            />
            <Button 
              type="submit" 
              disabled={loading || !input.trim()} 
              size="lg"
              className="w-full sm:w-auto px-8"
            >
              <Send size={18} className="mr-2" />
              Generate Solution
            </Button>
          </form>

          {/* Example Prompt */}
          <button
            type="button"
            onClick={handleExampleClick}
            className="group inline-flex items-start gap-3 text-left p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors max-w-lg mx-auto"
          >
            <Lightbulb className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <span className="text-sm font-medium text-foreground">Try an example</span>
              <p className="text-sm text-muted-foreground group-hover:text-foreground/80 transition-colors">
                "{EXAMPLE_PROMPT}"
              </p>
            </div>
          </button>
        </div>
      </div>
    );
  }

  // Workspace View - shown after first message
  return (
    <div className="h-[calc(100vh-64px)] flex flex-col md:flex-row">
      {/* Chat Panel */}
      <div className="w-full md:w-1/2 border-r border-border flex flex-col">
        <div className="p-6 border-b border-border">
          <h1 className="text-2xl font-bold font-display">{plannerLabel}</h1>
          <p className="text-muted-foreground mt-2">Describe your nonprofit's challenge and visualize an AI solution</p>
        </div>

        <ScrollArea className="flex-grow p-4">
          <div className="space-y-4">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-lg ${
                    msg.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-muted text-muted-foreground p-3 rounded-lg">
                  <p className="text-sm">Analyzing and generating solution...</p>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        <div className="p-4 border-t border-border space-y-2">
          {/* Mobile: View Workflow Button */}
          {workflowData && (
            <Button 
              onClick={() => setShowMobileWorkflow(true)}
              variant="outline"
              className="w-full md:hidden mb-2"
            >
              <Workflow className="mr-2 h-4 w-4" />
              View Workflow Diagram
            </Button>
          )}
          
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSend();
            }}
            className="space-y-2"
          >
            <Textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a follow-up question or describe another challenge..."
              disabled={loading}
              className="min-h-[80px] resize-none"
            />
            <Button type="submit" disabled={loading || !input.trim()} className="w-full">
              <Send size={18} className="mr-2" />
              Send
            </Button>
          </form>
        </div>
      </div>

      {/* Desktop: Visualization Panel */}
      <div className="hidden md:flex md:w-1/2 bg-card/30 flex-col">
        <div className="flex-1 overflow-auto">
          <SandboxCanvas 
            workflowData={workflowData} 
            analysis={analysis}
            summary={summary}
          />
        </div>
        {workflowData && (
          <div className="p-4 border-t border-border bg-card">
            <Button 
              onClick={handleRefineClick}
              variant="outline"
              className="w-full"
              disabled={loading}
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Refine This Idea
            </Button>
          </div>
        )}
      </div>

      {/* Mobile: Workflow Modal */}
      <Dialog open={showMobileWorkflow} onOpenChange={setShowMobileWorkflow}>
        <DialogContent className="max-w-[95vw] h-[90vh] flex flex-col p-0">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle>Workflow Diagram</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            <SandboxCanvas 
              workflowData={workflowData} 
              analysis={analysis}
              summary={summary}
            />
          </div>
          {workflowData && (
            <div className="p-4 border-t border-border">
              <Button 
                onClick={() => {
                  setShowMobileWorkflow(false);
                  handleRefineClick();
                }}
                variant="outline"
                className="w-full"
                disabled={loading}
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Refine This Idea
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Sandbox;
